import { OFFER_SECTION_MARKUP } from "./markup";
import { translate } from '../locale';
import { OfferController, type OfferAction, type OfferSnapshot, type OfferViewPort } from "./offerController";
import { ApiPricingPort, OFFER_PLAN_MONTHS, type OfferPlanMonths, type PricingPort } from "./pricing";

interface PlanNodes {
  readonly card: HTMLElement;
  readonly total: HTMLOutputElement;
  readonly monthly: HTMLElement;
  readonly saving: HTMLElement;
  readonly badge: HTMLElement;
  readonly availability: HTMLElement;
}

const required = <ElementType extends Element>(root: ParentNode, selector: string): ElementType => {
  const node = root.querySelector<ElementType>(selector);
  if (!node) throw new Error(`Offer markup is missing ${selector}.`);
  return node;
};

interface MountedRegion {
  readonly root: HTMLElement;
  readonly original: string;
}

/** Preserve the page section nodes: the director owns their geometry and datasets. */
const mountContents = (root: HTMLElement, markup: string): MountedRegion => {
  const original = root.innerHTML;
  const template = root.ownerDocument.createElement("template");
  template.innerHTML = markup;
  const contents = template.content.firstElementChild;
  if (!contents) throw new Error("Offer template is empty.");
  root.replaceChildren(...Array.from(contents.childNodes));
  return { root, original };
};

export class DomOfferView implements OfferViewPort {
  private readonly pricingRoot: HTMLElement;
  private readonly status: HTMLElement;
  private readonly registrationTarget: HTMLElement;
  private readonly plans = new Map<OfferPlanMonths, PlanNodes>();
  private readonly regions: MountedRegion[];
  private readonly disposers: (() => void)[] = [];
  private readonly registrationProvenance: string | null;
  private readonly verifiedRegistrationHref: string | null;

  constructor(root: ParentNode) {
    this.pricingRoot = required(root, "[data-offer-pricing]");
    const installRoot = required<HTMLElement>(root, "[data-offer-install]");
    const footerRoot = root.querySelector<HTMLElement>("[data-offer-footer]");
    const verifiedRegistration = this.pricingRoot.querySelector<HTMLAnchorElement>("a[data-verified-registration]");
    this.registrationProvenance = verifiedRegistration?.getAttribute("data-registration-source") ?? null;
    this.verifiedRegistrationHref = verifiedRegistration?.href ?? null;
    this.regions = [
      mountContents(this.pricingRoot, OFFER_SECTION_MARKUP.pricing),
      mountContents(installRoot, OFFER_SECTION_MARKUP.install),
      ...(footerRoot ? [mountContents(footerRoot, OFFER_SECTION_MARKUP.footer)] : []),
    ];
    this.status = required(this.pricingRoot, "[data-offer-pricing-status]");
    this.registrationTarget = required(this.pricingRoot, "[data-offer-registration-target]");
    for (const months of OFFER_PLAN_MONTHS) {
      const card = required<HTMLElement>(this.pricingRoot, `[data-offer-plan="${months}"]`);
      this.plans.set(months, {
        card,
        total: required(card, "[data-offer-total]"),
        monthly: required(card, "[data-offer-monthly]"),
        saving: required(card, "[data-offer-saving]"),
        badge: required(card, "[data-offer-recommended]"),
        availability: required(card, "[data-offer-plan-availability]"),
      });
      /*
       * Слушатели живут на карточке, а не на самой кнопке. Когда оформление
       * открыто, кнопка заменяется настоящей ссылкой, и обработчики,
       * привязанные к прежнему узлу, ушли бы вместе с ним.
       */
      const action = (): HTMLElement | null => card.querySelector<HTMLElement>("[data-offer-select]");
      const enabled = (): boolean => {
        const node = action();
        return !!node && !(node instanceof HTMLButtonElement && node.disabled);
      };
      const onSelect = (event: Event): void => {
        const node = action();
        if (!node || !(event.target instanceof Node) || !node.contains(event.target)) return;
        // У ссылки состояния «выбрано» нет: нажатие уводит на оформление.
        if (node instanceof HTMLButtonElement && !node.disabled) this.selectPlan(months);
      };
      const onPointer = (event: PointerEvent): void => {
        if (!enabled() || event.pointerType === 'touch') return;
        this.pricingRoot.dataset.offerHover=String(months);
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--offer-pointer-x',`${event.clientX-rect.left}px`);
        card.style.setProperty('--offer-pointer-y',`${event.clientY-rect.top}px`);
      };
      const onLeave = (): void => {
        delete this.pricingRoot.dataset.offerHover;
        card.style.removeProperty('--offer-pointer-x');card.style.removeProperty('--offer-pointer-y');
      };
      const onFocus=():void=>{if(enabled())this.pricingRoot.dataset.offerHover=String(months);};
      card.addEventListener("click", onSelect);
      card.addEventListener('focusin',onFocus);card.addEventListener('focusout',onLeave);
      card.addEventListener('pointermove',onPointer);card.addEventListener('pointerleave',onLeave);
      this.disposers.push(() => {card.removeEventListener("click", onSelect);card.removeEventListener('focusin',onFocus);card.removeEventListener('focusout',onLeave);card.removeEventListener('pointermove',onPointer);card.removeEventListener('pointerleave',onLeave);});
    }
  }

  render(snapshot: OfferSnapshot): void {
    const ready = snapshot.pricing.status === "ready";
    this.pricingRoot.dataset.pricingState = snapshot.pricing.status;
    this.pricingRoot.setAttribute("aria-busy", String(snapshot.pricing.status === "loading"));
    // Announce loading/errors without rendering service metadata beside the prices.
    this.status.textContent = snapshot.pricing.status === 'loading'?translate('Загрузка тарифов…'):snapshot.pricing.status==='error'?translate('Тарифы временно недоступны. Попробуй позже.'):'';
    this.renderRegistration(snapshot.trial);
    const baseline=snapshot.pricing.plans.find(plan=>plan.months===1)?.totalMinor;
    const currency=snapshot.pricing.currency;
    const formatter=currency?new Intl.NumberFormat(snapshot.pricing.locale,{style:'currency',currency,minimumFractionDigits:0,maximumFractionDigits:2}):null;
    for (const plan of snapshot.pricing.plans) {
      const nodes = this.plans.get(plan.months);
      if (!nodes) continue;
      nodes.total.textContent = plan.total;
      nodes.monthly.textContent = ready&&formatter&&plan.totalMinor!=null?translate('{amount} / месяц',{amount:formatter.format(plan.totalMinor/plan.months/100)}):'—';
      const saved=ready&&baseline!=null&&plan.totalMinor!=null?baseline*plan.months-plan.totalMinor:0;
      const saving=saved>0&&formatter?formatter.format(saved/100):'';
      nodes.saving.textContent=saving?translate('Экономия {amount}',{amount:saving}):'';
      nodes.saving.title=saving?translate('По сравнению с помесячной оплатой за тот же срок'):'';
      this.renderPlanAction(nodes, plan.months, ready ? plan.action : { kind: "text", label: "" });
      nodes.badge.hidden = !plan.recommended;
      nodes.card.toggleAttribute("data-recommended", plan.recommended);
    }
  }

  /*
   * Действие карточки.
   *
   * Пока адрес оформления не задан, это выключенная кнопка с подписью «Скоро»
   * — то же состояние, что было до появления оплаты. Как только адрес есть,
   * на её месте оказывается обычная ссылка: её можно открыть в новой вкладке,
   * скопировать и увидеть в строке состояния браузера, а кнопка со скриптом
   * ничего этого не умеет.
   */
  private renderPlanAction(nodes: PlanNodes, months: OfferPlanMonths, action: OfferAction): void {
    const document = nodes.card.ownerDocument;
    const current = nodes.card.querySelector<HTMLElement>("[data-offer-select]");
    const label = translate(action.kind === "link" ? action.label : "Выбрать тариф");
    const href = action.kind === "link" ? action.href : null;
    nodes.availability.textContent = href ? "" : translate("Скоро");
    nodes.availability.hidden = !!href;
    if (href) {
      nodes.card.dataset.offerSelectLabel = label;
      nodes.card.dataset.offerSelectedLabel = label;
    } else {
      delete nodes.card.dataset.offerSelectLabel;
      delete nodes.card.dataset.offerSelectedLabel;
      nodes.card.removeAttribute("data-selected");
    }
    if (current instanceof HTMLAnchorElement && href) {
      if (current.getAttribute("href") !== href) current.href = href;
      current.setAttribute("aria-label", label);
      return;
    }
    if (current instanceof HTMLButtonElement && !href) {
      current.disabled = true;
      return;
    }
    const replacement = document.createElement(href ? "a" : "button");
    replacement.className = "offer-plan__select";
    replacement.dataset.offerSelect = String(months);
    replacement.setAttribute("aria-labelledby", `offer-plan-${months}-title`);
    const caption = document.createElement("span");
    caption.className = "offer-sr-only";
    caption.textContent = label;
    replacement.append(caption);
    if (replacement instanceof HTMLAnchorElement) {
      replacement.href = href!;
      replacement.setAttribute("aria-label", label);
    } else if (replacement instanceof HTMLButtonElement) {
      replacement.type = "button";
      replacement.disabled = true;
      replacement.setAttribute("aria-pressed", "false");
    }
    current?.replaceWith(replacement);
  }

  destroy(): void {
    for (const dispose of this.disposers) dispose();
    for (const region of this.regions) region.root.innerHTML = region.original;
    delete this.pricingRoot.dataset.pricingState;
    delete this.pricingRoot.dataset.offerSelected;
    delete this.pricingRoot.dataset.offerHover;
    this.pricingRoot.removeAttribute("aria-busy");
  }

  private selectPlan(months: OfferPlanMonths): void {
    this.pricingRoot.dataset.offerSelected = String(months);
    for (const [term, nodes] of this.plans) {
      const selected = term === months;
      nodes.card.querySelector("[data-offer-select]")?.setAttribute("aria-pressed", String(selected));
      nodes.card.toggleAttribute("data-selected", selected);
    }
  }

  private renderRegistration(action: OfferAction): void {
    const enabled = import.meta.env.VITE_BB_TRIAL_ENABLED === "true";
    const canStart = enabled && action.kind === "link";
    const target = action.kind === "link" ? new URL(action.href) : null;
    if (target) target.searchParams.set("trial", "1");
    const href = canStart ? target!.href : "";
    const current = this.registrationTarget.firstElementChild;
    const tag = canStart ? "A" : "BUTTON";
    const label = translate(canStart ? action.label : "Начать 7 дней бесплатно");
    if (current?.tagName === tag && current.getAttribute("href") === (href || null)) {
      current.textContent = label;
      const availability = this.registrationTarget.querySelector("span");
      if (availability) availability.textContent = translate("Скоро");
      return;
    }
    if (canStart) {
      const anchor = this.registrationTarget.ownerDocument.createElement("a");
      anchor.className = "offer-action offer-action--primary";
      anchor.textContent = label;
      anchor.href = href;
      if (action.href === this.verifiedRegistrationHref) {
        anchor.setAttribute("data-verified-registration", "");
        if (this.registrationProvenance) anchor.setAttribute("data-registration-source", this.registrationProvenance);
      }
      this.registrationTarget.replaceChildren(anchor);
    } else {
      const button = this.registrationTarget.ownerDocument.createElement("button");
      button.className = "offer-action offer-action--primary";
      button.textContent = label;
      button.type = "button";
      button.disabled = true;
      const availability = this.registrationTarget.ownerDocument.createElement("span");
      availability.textContent = translate("Скоро");
      this.registrationTarget.replaceChildren(button, availability);
    }
  }
}

export const createOfferController = (
  root: ParentNode,
  pricingPort: PricingPort = new ApiPricingPort(),
): OfferController => new OfferController(pricingPort, new DomOfferView(root));
